import { createReducer, on } from '@ngrx/store';

import {
  SurveyCreationAudienceActions,
  SurveyCreationChildrenApiActions,
  SurveyCreationMcApiActions,
  SurveyCreationTrackerApiActions,
} from '../audience/survey-creation-audience.actions';
import { SurveyCreationLaunchActions } from '../launch/survey-creation-launch.actions';
import {
  SurveyCreationCoreActions,
  SurveyCreationCoreApiActions,
} from '../core/survey-creation-core.actions';
import { omitChild } from '../audience/utils/audience-children.helper';
import { coreContextOf } from '../../utils/core-context/survey-creation-action-core-context.helper';
import {
  DEFAULT_CHILD_INSIGHTS_STATE,
  INITIAL_SURVEY_CREATION_INSIGHTS_STATE,
} from './survey-creation-insights-state.constants';
import { InsightsState } from './survey-creation-insights-state.model';
import {
  AdvancedFilterActions,
  AdvancedFilterApiActions,
  SurveyCreationInsightsActions,
  SurveyCreationInsightsApiActions,
  SurveyCreationInsightsPreDraftApiActions,
} from './survey-creation-insights.actions';
import {
  initializeInsightsState,
  isLoadedInsightsSibling,
  patchChildren,
  patchInsightsChild,
} from './utils/insights-children.helper';
import {
  CHILD_SCOPE,
  delegateRecordToDefaultChild,
} from '../survey-creation-child-registry.helper';
import { DEFAULT_SURVEY_CHILD_KEY } from '../survey-creation-state.constants';

export const insightsReducer = createReducer(
  INITIAL_SURVEY_CREATION_INSIGHTS_STATE,

  on(
    SurveyCreationMcApiActions.deleteSurveyChildSuccess,
    SurveyCreationTrackerApiActions.deleteWaveSuccess,
    (state, { surveyId }): InsightsState => ({
      ...state,
      children: omitChild(state.children, surveyId),
    }),
  ),

  // An added locale has no insights of its own until they are recalculated, so
  // it starts from the default rather than reading as a child with no entry.
  on(
    SurveyCreationMcApiActions.addSurveyChildSuccess,
    SurveyCreationTrackerApiActions.addWaveSuccess,
    (state, { survey }): InsightsState => ({
      ...state,
      children: {
        ...state.children,
        [survey.id]: DEFAULT_CHILD_INSIGHTS_STATE,
      },
    }),
  ),

  on(
    SurveyCreationChildrenApiActions.updateChildSurveyCpiSuccess,
    (state, { survey }): InsightsState =>
      patchInsightsChild(state, survey.id, (child) => ({
        pricing: {
          ...child.pricing,
          offerPrice: survey.offerPrice ?? child.pricing.offerPrice,
        },
      })),
  ),

  // The feasibility CPI is an estimate the buyer never edits, but every save
  // transmits it — and a survey past draft never recalculates it, so nothing
  // else would put a value here. Seed it from the survey the API just returned
  // so a save sends back what it was given instead of a zero.
  on(
    SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess,
    (state, action): InsightsState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          feasibility: {
            ...child.feasibility,
            cpi: action.survey.cpi?.feasibilityCpi ?? child.feasibility.cpi,
          },
        }),
      ),
  ),

  on(
    SurveyCreationInsightsApiActions.computeScorePending,
    (state, action): InsightsState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          score: { ...child.score, loading: true, error: null },
        }),
      ),
  ),

  on(
    SurveyCreationInsightsApiActions.computeScoreSuccess,
    (state, action): InsightsState =>
      patchChildren(state, coreContextOf(action), CHILD_SCOPE.ACTIVE, {
        score: {
          scorePercentage: action.scorePercentage,
          loading: false,
          error: null,
        },
      }),
  ),

  on(
    SurveyCreationInsightsApiActions.computeScoreFailure,
    (state, action): InsightsState =>
      patchChildren(state, coreContextOf(action), CHILD_SCOPE.ACTIVE, {
        score: { scorePercentage: null, loading: false, error: action.error },
      }),
  ),

  on(
    SurveyCreationInsightsApiActions.calculateFeasibilityPending,
    (state, { key }): InsightsState =>
      patchInsightsChild(state, key, (child) => ({
        feasibility: { ...child.feasibility, loading: true, error: null },
      })),
  ),

  on(
    SurveyCreationInsightsApiActions.calculateFeasibilitySuccess,
    (
      state,
      { key, total, quotaGroups, audience, cpi, suppliers },
    ): InsightsState =>
      patchInsightsChild(state, key, (child) => ({
        feasibility: {
          ...child.feasibility,
          total,
          quotaGroups,
          audience,
          cpi,
          suppliers,
          loading: false,
          error: null,
        },
      })),
  ),

  on(
    SurveyCreationInsightsApiActions.calculateFeasibilityFailure,
    (state, { key, error }): InsightsState =>
      patchInsightsChild(state, key, (child) => ({
        feasibility: { ...child.feasibility, loading: false, error },
      })),
  ),

  on(
    SurveyCreationInsightsPreDraftApiActions.calculateFeasibilityForPreDraftPending,
    (state, { key }): InsightsState =>
      patchInsightsChild(state, key, (child) => ({
        feasibility: { ...child.feasibility, loading: true, error: null },
      })),
  ),

  on(
    SurveyCreationInsightsPreDraftApiActions.calculateFeasibilityForPreDraftSuccess,
    (
      state,
      { key, total, quotaGroups, audience, cpi, suppliers },
    ): InsightsState =>
      patchInsightsChild(state, key, (child) => ({
        feasibility: {
          ...child.feasibility,
          total,
          quotaGroups,
          audience,
          cpi,
          suppliers,
          loading: false,
          error: null,
        },
      })),
  ),

  on(
    SurveyCreationInsightsPreDraftApiActions.calculateFeasibilityForPreDraftFailure,
    (state, { key, error }): InsightsState =>
      patchInsightsChild(state, key, (child) => ({
        feasibility: { ...child.feasibility, loading: false, error },
      })),
  ),

  on(
    AdvancedFilterActions.clearSurveyOptions,
    (state, action): InsightsState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          feasibility: {
            ...child.feasibility,
            advancedFiltersLookup: {
              ...child.feasibility.advancedFiltersLookup,
              criteriaSurveyOptions: [],
              groupSurveyOptions: [],
              error: null,
            },
          },
        }),
      ),
  ),

  on(
    AdvancedFilterActions.resetFilters,
    (state, action): InsightsState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          feasibility: {
            ...child.feasibility,
            advancedFiltersLookup:
              DEFAULT_CHILD_INSIGHTS_STATE.feasibility.advancedFiltersLookup,
          },
        }),
      ),
  ),

  on(
    AdvancedFilterApiActions.findSurveysSuccess,
    (state, action): InsightsState => {
      const { surveys, psidRefsBySurveyId } = action;
      return patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          feasibility: {
            ...child.feasibility,
            advancedFiltersLookup: {
              ...child.feasibility.advancedFiltersLookup,
              matchedGroups: surveys,
              psidRefsBySurveyId: {
                ...child.feasibility.advancedFiltersLookup.psidRefsBySurveyId,
                ...psidRefsBySurveyId,
              },
              error: null,
            },
          },
        }),
      );
    },
  ),

  on(
    AdvancedFilterApiActions.findSurveysFailure,
    (state, action): InsightsState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          feasibility: {
            ...child.feasibility,
            advancedFiltersLookup: {
              ...child.feasibility.advancedFiltersLookup,
              error: action.error,
            },
          },
        }),
      ),
  ),

  on(
    AdvancedFilterApiActions.respondentCountsSuccess,
    (state, action): InsightsState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          feasibility: {
            ...child.feasibility,
            advancedFiltersLookup: {
              ...child.feasibility.advancedFiltersLookup,
              respondentCount: action.totalCount,
              error: null,
            },
          },
        }),
      ),
  ),

  on(
    AdvancedFilterApiActions.respondentCountsFailure,
    (state, action): InsightsState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          feasibility: {
            ...child.feasibility,
            advancedFiltersLookup: {
              ...child.feasibility.advancedFiltersLookup,
              respondentCount: 0,
              error: action.error,
            },
          },
        }),
      ),
  ),

  on(
    AdvancedFilterApiActions.searchCriteriaSurveysSuccess,
    (state, action): InsightsState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          feasibility: {
            ...child.feasibility,
            advancedFiltersLookup: {
              ...child.feasibility.advancedFiltersLookup,
              criteriaSurveyOptions: action.surveys,
              error: null,
            },
          },
        }),
      ),
  ),

  on(
    AdvancedFilterApiActions.searchCriteriaSurveysFailure,
    (state, action): InsightsState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          feasibility: {
            ...child.feasibility,
            advancedFiltersLookup: {
              ...child.feasibility.advancedFiltersLookup,
              error: action.error,
            },
          },
        }),
      ),
  ),

  on(
    AdvancedFilterApiActions.searchGroupSurveysSuccess,
    (state, action): InsightsState => {
      const { surveys, psidRefsBySurveyId } = action;
      return patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          feasibility: {
            ...child.feasibility,
            advancedFiltersLookup: {
              ...child.feasibility.advancedFiltersLookup,
              groupSurveyOptions: surveys,
              psidRefsBySurveyId: {
                ...child.feasibility.advancedFiltersLookup.psidRefsBySurveyId,
                ...psidRefsBySurveyId,
              },
              error: null,
            },
          },
        }),
      );
    },
  ),

  on(
    AdvancedFilterApiActions.searchGroupSurveysFailure,
    (state, action): InsightsState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          feasibility: {
            ...child.feasibility,
            advancedFiltersLookup: {
              ...child.feasibility.advancedFiltersLookup,
              error: action.error,
            },
          },
        }),
      ),
  ),

  // Also hydrates pricing.offerPrice/surveyPauseThreshold from these prices —
  // this fires on survey load and every recalculation, so a save made without
  // ever opening the CPI drawer still carries the survey's existing pricing
  // instead of wiping it with `null`. Apply overwrites it afterwards.
  on(
    SurveyCreationInsightsApiActions.calculateFlexibilityPricesSuccess,
    (state, { key, prices }): InsightsState =>
      patchInsightsChild(state, key, (child) => ({
        flexibilityPrices: { prices, error: null },
        pricing: {
          ...child.pricing,
          offerPrice: prices.surveyPriceOverride,
          surveyPauseThreshold: prices.surveyPauseThreshold,
        },
      })),
  ),

  on(
    SurveyCreationInsightsActions.applyCpiPricing,
    (state, action): InsightsState => {
      // The blend flag belongs to the active child; the offer price reaches the whole scope.
      const context = coreContextOf(action);
      const withActiveBlend = patchChildren(
        state,
        context,
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          pricing: { ...child.pricing, blendApplied: action.blendApplied },
        }),
      );

      return patchChildren(withActiveBlend, context, action.scope, (child) => ({
        pricing: { ...child.pricing, offerPrice: action.offerPrice },
      }));
    },
  ),

  // The auto PurePrice recompute writes only offerPrice; the pause threshold
  // and blend flag it did not compute are kept as-is.
  on(
    SurveyCreationInsightsApiActions.loadPurePriceCpiSuccess,
    (state, action): InsightsState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          pricing: {
            ...child.pricing,
            offerPrice: action.cpi,
          },
        }),
      ),
  ),

  // The auto PurePrice recompute writes only offerPrice; the pause threshold
  // and blend flag it did not compute are kept as-is.
  on(
    SurveyCreationInsightsApiActions.loadPurePriceCpiSuccess,
    (state, action): InsightsState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          pricing: {
            ...child.pricing,
            offerPrice: action.cpi,
          },
        }),
      ),
  ),

  // A managed Apply persists the survey-wide global override; mirror the applied
  // offer price + pause threshold onto the active child so pricing.offerPrice stays
  // in step with suppliersMetaData.priceOverride. blendApplied is kept as-is, and
  // an omitted threshold (not submitted) keeps the one already there.
  on(
    SurveyCreationInsightsApiActions.syncGlobalPriceOverride,
    (state, action): InsightsState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          pricing: {
            ...child.pricing,
            offerPrice: action.offerPrice,
            surveyPauseThreshold:
              action.surveyPauseThreshold ?? child.pricing.surveyPauseThreshold,
          },
        }),
      ),
  ),

  on(
    SurveyCreationInsightsApiActions.calculateFlexibilityPricesFailure,
    (state, { key, error }): InsightsState =>
      patchInsightsChild(state, key, {
        flexibilityPrices: { prices: null, error },
        pricing: { ...DEFAULT_CHILD_INSIGHTS_STATE.pricing },
      }),
  ),

  on(
    SurveyCreationInsightsApiActions.loadSimilarSurveysPending,
    (state, action): InsightsState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          similarSurveys: {
            ...child.similarSurveys,
            status: 'loading',
            error: null,
            isStale: false,
            searchedGlobally: action.searchGlobally,
          },
        }),
      ),
  ),

  on(
    SurveyCreationInsightsApiActions.loadSimilarSurveysSuccess,
    (state, action): InsightsState => {
      const { surveys, searchGlobally, targetingKey, isStale } = action;
      return patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          similarSurveys: {
            surveys,
            status: 'loaded',
            error: null,
            isStale,
            searchedTargetingKey: targetingKey,
            searchedGlobally: searchGlobally,
            globalSearchUsed:
              child.similarSurveys.globalSearchUsed || searchGlobally,
          },
        }),
      );
    },
  ),

  on(
    SurveyCreationInsightsApiActions.loadSimilarSurveysFailure,
    (state, action): InsightsState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          similarSurveys: {
            ...child.similarSurveys,
            status: 'failed',
            error: action.error,
            isStale: false,
          },
        }),
      ),
  ),

  on(
    SurveyCreationInsightsActions.cancelSimilarSurveysSearch,
    (state, action): InsightsState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          similarSurveys: {
            ...child.similarSurveys,
            status: child.similarSurveys.searchedTargetingKey
              ? 'loaded'
              : 'idle',
            error: null,
            isStale: !!child.similarSurveys.searchedTargetingKey,
          },
        }),
      ),
  ),

  on(
    SurveyCreationInsightsActions.markSimilarSurveysStale,
    (state, action): InsightsState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          similarSurveys: { ...child.similarSurveys, isStale: true },
        }),
      ),
  ),

  // Cross-listens to both stage-specific init actions — a genuinely new
  // target resets insights fresh (mirrors `initializeCoreState`); an
  // already-targeted repeat (e.g. a locale switch) keeps the computed state.
  on(
    SurveyCreationAudienceActions.init,
    SurveyCreationLaunchActions.init,
    (state, action): InsightsState => {
      const { surveyId } = action;
      const { rootSurveyId } = coreContextOf(action);
      const alreadyTargeted = !!surveyId && rootSurveyId === surveyId;

      if (alreadyTargeted) {
        return state;
      }

      if (!!surveyId && isLoadedInsightsSibling(state.children, surveyId)) {
        return state;
      }

      return initializeInsightsState(surveyId);
    },
  ),

  on(
    SurveyCreationCoreActions.resetActiveSurveyId,
    (state, action): InsightsState => ({
      ...state,
      children: delegateRecordToDefaultChild(
        state.children,
        coreContextOf(action).activeChildKey ?? DEFAULT_SURVEY_CHILD_KEY,
      ),
    }),
  ),

  on(
    SurveyCreationInsightsApiActions.loadEpcEstimatePending,
    (state, action): InsightsState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          epc: {
            platformByCountry: child.epc.platformByCountry,
            estimate: child.epc.estimate,
            actual: child.epc.actual,
            estimateLoading: true,
            error: null,
            requestedQuery: action.query,
          },
        }),
      ),
  ),

  on(
    SurveyCreationInsightsApiActions.clearEpcEstimate,
    (state, action): InsightsState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          epc: {
            platformByCountry: child.epc.platformByCountry,
            estimate: null,
            actual: child.epc.actual,
            estimateLoading: false,
            error: null,
            requestedQuery: null,
          },
        }),
      ),
  ),

  on(
    SurveyCreationInsightsApiActions.loadEpcEstimateSuccess,
    (state, action): InsightsState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          epc: {
            platformByCountry: child.epc.platformByCountry,
            estimate: action.estimate,
            actual: child.epc.actual,
            estimateLoading: false,
            error: null,
            requestedQuery: child.epc.requestedQuery,
          },
        }),
      ),
  ),

  on(
    SurveyCreationInsightsApiActions.loadEpcEstimateFailure,
    (state, action): InsightsState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          epc: {
            platformByCountry: child.epc.platformByCountry,
            estimate: null,
            actual: child.epc.actual,
            estimateLoading: false,
            error: action.error,
            requestedQuery: child.epc.requestedQuery,
          },
        }),
      ),
  ),

  on(
    SurveyCreationInsightsApiActions.loadEpcPlatformSuccess,
    (state, action): InsightsState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          epc: {
            platformByCountry: {
              ...child.epc.platformByCountry,
              [action.countryCode]: action.platform,
            },
            estimate: child.epc.estimate,
            actual: child.epc.actual,
            estimateLoading: child.epc.estimateLoading,
            error: null,
            requestedQuery: child.epc.requestedQuery,
          },
        }),
      ),
  ),

  on(
    SurveyCreationInsightsApiActions.loadEpcPlatformFailure,
    (state, action): InsightsState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          epc: {
            // The record is kept intact on purpose: one country failing to resolve
            // must not discard the countries already fetched.
            platformByCountry: child.epc.platformByCountry,
            estimate: child.epc.estimate,
            actual: child.epc.actual,
            estimateLoading: child.epc.estimateLoading,
            error: action.error,
            requestedQuery: child.epc.requestedQuery,
          },
        }),
      ),
  ),

  on(
    SurveyCreationInsightsApiActions.loadEpcActualSuccess,
    (state, action): InsightsState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          epc: {
            platformByCountry: child.epc.platformByCountry,
            estimate: child.epc.estimate,
            actual: action.actual,
            estimateLoading: child.epc.estimateLoading,
            error: null,
            requestedQuery: child.epc.requestedQuery,
          },
        }),
      ),
  ),

  on(
    SurveyCreationInsightsApiActions.loadEpcActualFailure,
    (state, action): InsightsState =>
      patchChildren(
        state,
        coreContextOf(action),
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          epc: {
            platformByCountry: child.epc.platformByCountry,
            estimate: child.epc.estimate,
            actual: null,
            estimateLoading: child.epc.estimateLoading,
            error: action.error,
            requestedQuery: child.epc.requestedQuery,
          },
        }),
      ),
  ),

  on(
    SurveyCreationInsightsApiActions.findImprobableQuotasSuccess,
    (state, action): InsightsState =>
      patchChildren(state, coreContextOf(action), CHILD_SCOPE.ACTIVE, {
        improbableQuotas: { quotaIds: action.quotaIds, error: null },
      }),
  ),

  on(
    SurveyCreationInsightsApiActions.findImprobableQuotasFailure,
    (state, action): InsightsState =>
      // A failed lookup is not a verdict — clear the ids rather than leave the
      // banner asserting something this response never said.
      patchChildren(state, coreContextOf(action), CHILD_SCOPE.ACTIVE, {
        improbableQuotas: { quotaIds: [], error: action.error },
      }),
  ),

  on(
    SurveyCreationCoreActions.reset,
    (): InsightsState => INITIAL_SURVEY_CREATION_INSIGHTS_STATE,
  ),
);
